/**
 * Náhľad balíčka postavy. Karty sú v obsahu na klientovi, takže sa dá pozrieť
 * ešte pred založením hry — bez toho si hráč vyberá postavu naslepo.
 */

import type { CardDef, HeroDef } from '@unmatched/engine';
import { content, localizeHero } from '@unmatched/content';
import { IMPLEMENTED_HEROES } from '@unmatched/engine';
import { useEffect, useMemo, useState } from 'react';

import { artOf } from '../screens/Home.js';
import { Card } from './Card.js';
import { CardStyleToggle } from './CardStyleToggle.js';
import { CardPreview, type PreviewTarget } from './CardPreview.js';

const GROUPS: { type: CardDef['type']; label: string }[] = [
  { type: 'attack', label: 'Útok' },
  { type: 'defense', label: 'Obrana' },
  { type: 'versatile', label: 'Univerzál' },
  { type: 'scheme', label: 'Plán' },
];

interface Props {
  hero: HeroDef;
  onClose: () => void;
}

export function DeckDialog({ hero, onClose }: Props) {
  const deck = content.decks[hero.deckId];
  const { special } = localizeHero(hero, 'en');
  // Aj tu sa karta dá podržať pod kurzorom — v režime „Obrázok + popis" je to
  // jediná cesta, ako sa v balíčku dočítať, čo karta robí.
  const [preview, setPreview] = useState<PreviewTarget | null>(null);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const stats = useMemo(() => {
    const cards = deck?.cards ?? [];
    const total = cards.reduce((sum, card) => sum + card.qty, 0);
    const byType = new Map<CardDef['type'], number>();
    for (const card of cards) byType.set(card.type, (byType.get(card.type) ?? 0) + card.qty);

    // Univerzál sa počíta do útoku aj obrany — tak ho berú aj pravidlá.
    const offence = cards.filter((c) => c.type === 'attack' || c.type === 'versatile');
    const defence = cards.filter((c) => c.type === 'defense' || c.type === 'versatile');
    const weighted = (list: CardDef[]) => {
      const count = list.reduce((sum, c) => sum + c.qty, 0);
      if (!count) return 0;
      return list.reduce((sum, c) => sum + c.value * c.qty, 0) / count;
    };

    return {
      total,
      byType,
      bestAttack: Math.max(0, ...offence.map((c) => c.value)),
      avgAttack: weighted(offence),
      avgDefence: weighted(defence),
      avgBoost: weighted(cards) && cards.reduce((s, c) => s + c.boost * c.qty, 0) / (total || 1),
    };
  }, [deck]);

  return (
    <div className="modal" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="modal__panel" onClick={(event) => event.stopPropagation()}>
        <header className="modal__head">
          <span className="heroChip__art deckDialog__art">
            {artOf(hero.id) ? (
              <img src={artOf(hero.id)} alt="" draggable={false} />
            ) : hero.icon ? (
              <img src={hero.icon} alt="" />
            ) : (
              hero.name.slice(0, 1)
            )}
          </span>
          <div>
            <h2 className="modal__title">{hero.name}</h2>
            <span className="modal__sub">
              {stats.total} kariet · {hero.hp} zdravia · pohyb {hero.move} ·{' '}
              {hero.attack === 'melee' ? 'útok zblízka' : 'útok na diaľku'}
            </span>
          </div>
          {/* Prepínač patrí aj sem — balíček sa prezerá z lobby, kde horná
              lišta zápasu ešte neexistuje. */}
          <div className="modal__tools">
            <CardStyleToggle />
            <button type="button" className="btn btn--ghost" onClick={onClose}>
              Zavrieť
            </button>
          </div>
        </header>

        <div className="deck__summary">
          <span className="chip">
            najsilnejší útok <b>{stats.bestAttack}</b>
          </span>
          <span className="chip">
            priemerný útok <b>{stats.avgAttack.toFixed(1)}</b>
          </span>
          <span className="chip">
            priemerná obrana <b>{stats.avgDefence.toFixed(1)}</b>
          </span>
          <span className="chip">
            priemerný boost <b>{stats.avgBoost.toFixed(1)}</b>
          </span>
        </div>

        {special ? (
          <div className="deck__special">
            <span className="eyebrow">Špeciálna schopnosť</span>
            <p>{special}</p>
          </div>
        ) : null}

        {/* Poctivé varovanie: efekty má zatiaľ hotových len časť postáv. */}
        {IMPLEMENTED_HEROES.includes(hero.id) ? (
          <p className="deck__warning deck__warning--ok">
            Táto postava má <strong>efekty kariet aj špeciálnu schopnosť funkčné</strong> — hrá sa
            podľa pravidiel.
          </p>
        ) : (
          <p className="deck__warning">
            Hodnoty kariet, boosty aj to, ktorá figúrka smie kartu zahrať, v hre fungujú.
            <strong> Texty efektov sa u tejto postavy zatiaľ nevyhodnocujú.</strong>
          </p>
        )}

        <div className="deck__body">
          {GROUPS.map((group) => {
            const cards = (deck?.cards ?? []).filter((card) => card.type === group.type);
            if (!cards.length) return null;
            return (
              <section key={group.type} className="deck__group">
                <span className="eyebrow">
                  {group.label} · {stats.byType.get(group.type) ?? 0} kariet
                </span>
                <div className="deck__cards">
                  {cards.map((card) => (
                    <div key={card.id} className="deckCard">
                      <Card
                        def={card}
                        large
                        onHover={(def, rect) =>
                          setPreview(def && rect ? { def, rect, place: 'beside' } : null)
                        }
                      />
                      <span className="deckCard__qty">{card.qty}×</span>
                    </div>
                  ))}
                </div>
              </section>
            );
          })}
        </div>

        <CardPreview target={preview} />
      </div>
    </div>
  );
}
